import { Fragment, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { PaginationBar } from "@/components/ui/PaginationBar";
import { ChevronDown, ChevronRight, MoreHorizontal, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useApprovePayment,
  useBulkReconcilePayments,
  usePrintPaymentReceipt,
  useProcessRefund,
  usePayments,
  useReconcilePayment,
  useRejectPayment,
} from "../hooks/usePayments";
import type { Payment } from "../services/paymentsApi";
import { PAYMENT_APPROVAL_STATUS } from "../services/paymentsApi";
import { useNotification } from "@/shared/hooks/useNotification";
import { useConfirm } from "@/shared/contexts/confirm-context";
import { openReceiptPdfForPrint } from "../services/paymentReceiptPdf";
import { ReprintPaymentReceiptDialog } from "../components/ReprintPaymentReceiptDialog";
import { toast } from "sonner";
import { getErrorMessage } from "@/shared/utils/errorHandler";

type PaymentsView = "all" | "pending-approval" | "unreconciled" | "refunds";
type PendingTypeFilter = "all" | "payment" | "refund";

const PAYMENT_METHODS = [
  "all",
  "Cash",
  "Bank Transfer",
  "Mobile Money",
  "Check",
  "Credit Card",
];

const getExpoPaymentLabel = (payment: Payment) =>
  payment.expoName || (payment.orderIsExpoSale || payment.expoId ? "Expo sale" : null);

const ExpoPaymentBadge = ({ label }: { label?: string | null }) =>
  label ? (
    <Badge
      variant="outline"
      className="w-fit border-cyan-300 bg-cyan-50 text-[10px] font-medium text-cyan-800"
    >
      Expo: {label}
    </Badge>
  ) : null;

const todayInNairobi = () => {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Africa/Nairobi", year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  const value = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${value.year}-${value.month}-${value.day}`;
};

export const PaymentsManagement = () => {
  const notification = useNotification();
  const confirm = useConfirm();

  // Allow deep-linking to a specific tab via ?view= (e.g. notifications link to
  // /payments?view=pending-approval). Falls back to "all" for unknown values.
  const [searchParams] = useSearchParams();
  const requestedView = searchParams.get("view");
  const requestedType = searchParams.get("type");
  const initialView: PaymentsView =
    requestedView === "pending-approval" ||
    requestedView === "unreconciled" ||
    requestedView === "refunds"
      ? requestedView
      : "all";
  const initialPendingType: PendingTypeFilter =
    requestedType === "refund" || requestedType === "payment"
      ? requestedType
      : "all";

  const [selectedPaymentIds, setSelectedPaymentIds] = useState<string[]>([]);
  const [activeView, setActiveView] = useState<PaymentsView>(initialView);
  const [pendingTypeFilter, setPendingTypeFilter] = useState<PendingTypeFilter>(initialPendingType);
  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearch = useDebounce(searchTerm, 300);
  const [paymentMethodFilter, setPaymentMethodFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [fromDate, setFromDate] = useState(todayInNairobi);
  const [toDate, setToDate] = useState(todayInNairobi);

  const apiView = {
    all: "All",
    "pending-approval": "PendingApproval",
    unreconciled: "Unreconciled",
    refunds: "Refunds",
  } as const;

  const { data: paymentsPage, isLoading, error } = usePayments({
    pageNumber: currentPage,
    pageSize,
    search: debouncedSearch.trim() || undefined,
    paymentMethod: paymentMethodFilter === "all" ? undefined : paymentMethodFilter,
    fromDate: fromDate || undefined,
    toDate: toDate || undefined,
    view: apiView[activeView],
    paymentType: activeView === "pending-approval" && pendingTypeFilter !== "all"
      ? pendingTypeFilter === "refund" ? "Refund" : "Payment"
      : undefined,
  });

  const reconcilePayment = useReconcilePayment();
  const bulkReconcilePayments = useBulkReconcilePayments();
  const processRefund = useProcessRefund();
  const printPaymentReceipt = usePrintPaymentReceipt();
  const approvePaymentMut = useApprovePayment();
  const rejectPaymentMut = useRejectPayment();

  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<string>>(new Set());

  const toggleOrderExpand = (orderId: string) => {
    setExpandedOrderIds((prev) => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  type PaymentGroup = {
    type: "group";
    orderId: string;
    orderNumber: string;
    customerName: string;
    totalAmount: number;
    payments: Payment[];
    approvedCount: number;
    allApproved: boolean;
    hasMixedMethods: boolean;
    orderFullyPaid: boolean;
    hasPrintableDocument: boolean;
    /** True when this is a walk-in order — gates the combined-receipt print
     *  button which is only meaningful for the walk-in single-PDF pipeline. */
    orderIsWalkIn: boolean;
    expoName?: string | null;
    orderIsExpoSale: boolean;
  };
  type FlatRow = { type: "flat"; payment: Payment };
  type DisplayRow = PaymentGroup | FlatRow;

  const [reconcileDialogOpen, setReconcileDialogOpen] = useState(false);
  const [activePayment, setActivePayment] = useState<Payment | null>(null);
  const [reconcileNotes, setReconcileNotes] = useState("");

  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundPayment, setRefundPayment] = useState<Payment | null>(null);
  const [reprintDialogOpen, setReprintDialogOpen] = useState(false);
  const [reprintPayment, setReprintPayment] = useState<Payment | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectPayment, setRejectPayment] = useState<Payment | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [refundForm, setRefundForm] = useState({
    amount: "",
    reason: "",
    paymentMethod: "",
    paymentReference: "",
    notes: "",
  });

  const hasBackendRows = paymentsPage !== undefined;
  const rows = useMemo(() => paymentsPage?.items ?? [], [paymentsPage]);
  const isRefundType = (payment: Payment) => payment.type.toLowerCase().includes("refund");
  const getPaymentDisplayLabel = (payment: Payment) =>
    isRefundType(payment) && payment.refundReason
      ? payment.refundReason
      : payment.paymentNumber;
  const filteredRows = rows;
  const paginatedRows = rows;

  const unreconciledRows = useMemo(
    () => rows.filter((payment) => !payment.isReconciled),
    [rows]
  );

  // Reset to page 1 when filters/view change — intentional sync of derived state from
  // filter inputs; the lint rule's general advice (derive instead) doesn't fit because
  // pagination is interactive state that must persist across filter-unchanged renders.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentPage(1);
  }, [debouncedSearch, paymentMethodFilter, activeView, pendingTypeFilter, fromDate, toDate]);

  const totalCount = paymentsPage?.totalCount ?? 0;
  const totalPages = paymentsPage?.totalPages ?? 1;

  const displayRows: DisplayRow[] = useMemo(() => {
    // Group payments by orderId so any order with ≥ 2 payments on this page
    // renders as an expandable parent row. Single-payment orders fall through
    // to a flat row. The walk-in path is no longer special — split-tender
    // happens on regular orders too now (cash + wallet, mpesa + wallet, etc.).
    const byOrder = new Map<string, Payment[]>();
    for (const payment of paginatedRows) {
      const existing = byOrder.get(payment.orderId);
      if (existing) existing.push(payment);
      else byOrder.set(payment.orderId, [payment]);
    }

    const result: DisplayRow[] = [];
    for (const payment of paginatedRows) {
      const group = byOrder.get(payment.orderId);
      if (!group) continue;

      if (group.length === 1) {
        result.push({ type: "flat", payment });
        byOrder.delete(payment.orderId);
        continue;
      }

      // Emit the group exactly once — on the first payment of that group we
      // encounter while iterating the original order.
      if (group[0].id === payment.id) {
        // Show combined tendered amount for the grouped rows (sum of legs),
        // not the order grand total.
        const groupedTenderAmount = group.reduce((sum, p) => sum + p.amount, 0);
        const approvedCount = group.filter(
          (p) => p.approvalStatus === PAYMENT_APPROVAL_STATUS.Approved ||
            p.approvalStatus === PAYMENT_APPROVAL_STATUS.PartiallyApproved
        ).length;
        const hasMixedMethods =
          new Set(
            group.map((p) => (p.paymentMethodAccountId ?? p.paymentMethod ?? "").toLowerCase())
          ).size > 1;
        result.push({
          type: "group",
          orderId: payment.orderId,
          orderNumber: payment.orderNumber,
          customerName: payment.customerName,
          totalAmount: groupedTenderAmount,
          payments: group,
          approvedCount,
          allApproved: approvedCount === group.length,
          hasMixedMethods,
          orderFullyPaid: group.every((p) => p.orderFullyPaid === true),
          hasPrintableDocument: group.some((p) => Boolean(p.invoiceNumber || p.receiptNumber)),
          orderIsWalkIn: group[0].orderIsWalkIn === true,
          expoName: group.map(getExpoPaymentLabel).find(Boolean) ?? null,
          orderIsExpoSale: group.some((p) => p.orderIsExpoSale === true || Boolean(p.expoId)),
        });
        byOrder.delete(payment.orderId);
      }
    }

    return result;
  }, [paginatedRows]);

  const allUnreconciledSelected =
    unreconciledRows.length > 0 &&
    unreconciledRows.every((payment) => selectedPaymentIds.includes(payment.id));

  const isBusy = reconcilePayment.isPending || bulkReconcilePayments.isPending;

  const resetRefundForm = () => {
    setRefundPayment(null);
    setRefundForm({
      amount: "",
      reason: "",
      paymentMethod: "",
      paymentReference: "",
      notes: "",
    });
  };

  const togglePaymentSelection = (paymentId: string, checked: boolean) => {
    setSelectedPaymentIds((prev) => {
      if (checked) {
        if (prev.includes(paymentId)) {
          return prev;
        }
        return [...prev, paymentId];
      }

      return prev.filter((id) => id !== paymentId);
    });
  };

  const handleSelectAllUnreconciled = (checked: boolean) => {
    if (checked) {
      setSelectedPaymentIds(unreconciledRows.map((payment) => payment.id));
      return;
    }

    setSelectedPaymentIds([]);
  };

  const openReconcileDialog = (payment: Payment) => {
    setActivePayment(payment);
    setReconcileNotes("");
    setReconcileDialogOpen(true);
  };

  const openRefundDialog = (payment: Payment) => {
    if (isRefundType(payment)) {
      notification.error("Refund transactions cannot be refunded again");
      return;
    }

    setRefundPayment(payment);
    setRefundForm({
      amount: String(payment.amount),
      reason: "",
      paymentMethod: payment.paymentMethod || "",
      paymentReference: payment.paymentReference || "",
      notes: "",
    });
    setRefundDialogOpen(true);
  };

  const openReprintDialog = (payment: Payment) => {
    setReprintPayment(payment);
    setReprintDialogOpen(true);
  };

  const handleReprintReceipt = (payload: {
    reprintReason: string;
    format: import("../services/paymentsApi").ReceiptFormat;
  }) => {
    if (!reprintPayment) {
      return;
    }

    printPaymentReceipt.mutate(
      {
        paymentId: reprintPayment.id,
        payload: {
          isReprint: true,
          reprintReason: payload.reprintReason,
          format: payload.format,
        },
      },
      {
        onSuccess: (receiptBlob) => {
          const opened = openReceiptPdfForPrint(receiptBlob);

          if (!opened) {
            notification.info("Enable pop-ups to open receipt for printing.");
            return;
          }

          notification.success(`Receipt reprint opened for ${reprintPayment.paymentNumber}`);
          setReprintDialogOpen(false);
          setReprintPayment(null);
        },
        onError: () => {
          notification.error("Failed to reprint receipt");
        },
      }
    );
  };

  const handleSingleReconcile = () => {
    if (!activePayment) {
      return;
    }

    reconcilePayment.mutate(
      {
        paymentId: activePayment.id,
        payload: {
          paymentId: activePayment.id,
          notes: reconcileNotes.trim() || undefined,
        },
      },
      {
        onSuccess: () => {
          notification.success(`Payment ${activePayment.paymentNumber} reconciled`);
          setReconcileDialogOpen(false);
          setActivePayment(null);
          setReconcileNotes("");
          setSelectedPaymentIds((prev) => prev.filter((id) => id !== activePayment.id));
        },
        onError: () => {
          notification.error("Failed to reconcile payment");
        },
      }
    );
  };

  const handleBulkReconcile = () => {
    if (!selectedPaymentIds.length) {
      return;
    }

    const count = selectedPaymentIds.length;
    const ids = selectedPaymentIds;
    confirm({
      title: "Reconcile selected payments?",
      description: `${count} payment${count > 1 ? "s" : ""} will be marked as reconciled.`,
      confirmLabel: "Reconcile",
      onConfirm: () =>
        bulkReconcilePayments
          .mutateAsync(ids)
          .then(() => {
            notification.success(
              `${count} payment${count > 1 ? "s" : ""} reconciled`
            );
            setSelectedPaymentIds([]);
          })
          .catch((e) => {
            notification.error("Failed to reconcile selected payments");
            throw e;
          }),
    });
  };

  const handleRefund = () => {
    if (!refundPayment) {
      return;
    }

    if (!refundForm.amount || Number(refundForm.amount) <= 0) {
      notification.error("Refund amount must be greater than 0");
      return;
    }

    if (!refundForm.reason.trim() || !refundForm.paymentMethod.trim()) {
      notification.error("Refund reason and payment method are required");
      return;
    }

    const refundAmount = Number(refundForm.amount);
    confirm({
      title: "Process refund?",
      description: `Refund against payment ${refundPayment.paymentNumber} for ${refundPayment.customerName}. This moves money out.`,
      variant: "destructive",
      confirmLabel: "Process refund",
      body: (
        <div className="space-y-1">
          <p className="flex justify-between">
            <span className="text-muted-foreground">Refund amount</span>
            <span className="font-medium">KES {refundAmount.toLocaleString()}</span>
          </p>
          <p className="flex justify-between">
            <span className="text-muted-foreground">Method</span>
            <span className="font-medium">{refundForm.paymentMethod}</span>
          </p>
          <p className="flex justify-between">
            <span className="text-muted-foreground">Reason</span>
            <span className="font-medium">{refundForm.reason.trim()}</span>
          </p>
        </div>
      ),
      onConfirm: () =>
        processRefund
          .mutateAsync({
            paymentId: refundPayment.id,
            amount: refundAmount,
            reason: refundForm.reason.trim(),
            paymentMethod: refundForm.paymentMethod,
            paymentReference: refundForm.paymentReference.trim() || undefined,
            notes: refundForm.notes.trim() || undefined,
          })
          .then((payment) => {
            notification.success(`Refund created for ${payment.paymentNumber}`);
            setRefundDialogOpen(false);
            resetRefundForm();
            setSelectedPaymentIds((prev) => prev.filter((id) => id !== payment.id));
          })
          .catch((e) => {
            notification.error("Failed to process refund");
            throw e;
          }),
    });
  };

  const handleApprovePayment = (payment: Payment) => {
    confirm({
      title: "Approve payment?",
      description: `Payment ${payment.paymentNumber} for ${payment.customerName} will be approved. A receipt preview will open when it's ready.`,
      confirmLabel: "Approve",
      pendingLabel: "Approving payment…",
      body: (
        <div className="space-y-1">
          <p className="flex justify-between">
            <span className="text-muted-foreground">Order</span>
            <span className="font-medium">{payment.orderNumber}</span>
          </p>
          <p className="flex justify-between">
            <span className="text-muted-foreground">Amount</span>
            <span className="font-medium">KES {payment.amount.toLocaleString()}</span>
          </p>
          <p className="flex justify-between">
            <span className="text-muted-foreground">Method</span>
            <span>{payment.paymentMethod}</span>
          </p>
        </div>
      ),
      onConfirm: () =>
        approvePaymentMut
          .mutateAsync({ paymentId: payment.id, payload: {} })
          .then((approved) => {
            if (approved.type === "Refund") {
              notification.success(`Refund ${approved.paymentNumber} approved.`);
              return;
            }

            // Walk-in split tender: the combined receipt only prints once every leg is
            // approved. If this leg leaves the order partially paid, hold the print —
            // the backend would also reject it, but skipping the round-trip is cleaner UX.
            if (approved.orderIsWalkIn && !approved.orderFullyPaid) {
              notification.success(
                `Payment ${approved.paymentNumber} approved — combined receipt will print once the remaining payment leg(s) are approved.`
              );
              return;
            }

            // Approval is complete at this point. Keep a visible loader while the
            // separate receipt request builds the PDF.
            const receiptToastId = toast.loading(
              `Payment ${approved.paymentNumber} approved. Preparing receipt…`
            );

            printPaymentReceipt.mutate(
              {
                paymentId: approved.id,
                payload: { isReprint: false },
              },
              {
                onSuccess: (receiptBlob) => {
                  const opened = openReceiptPdfForPrint(receiptBlob);
                  toast.success("Receipt is ready to print.", { id: receiptToastId });
                  if (!opened) {
                    notification.info("Enable pop-ups to open receipt for printing.");
                  }
                },
                onError: () => {
                  toast.error("Payment approved, but receipt preparation failed. Use Reprint Receipt.", {
                    id: receiptToastId,
                  });
                },
              }
            );
          })
          .catch((e) => {
            notification.error(getErrorMessage(e));
            throw e;
          }),
    });
  };

  const openRejectDialog = (payment: Payment) => {
    setRejectPayment(payment);
    setRejectReason("");
    setRejectDialogOpen(true);
  };

  const handleRejectPayment = () => {
    if (!rejectPayment) return;
    if (!rejectReason.trim()) {
      notification.error("Rejection reason is required");
      return;
    }

    rejectPaymentMut.mutate(
      { paymentId: rejectPayment.id, payload: { rejectionReason: rejectReason.trim() } },
      {
        onSuccess: (rejected) => {
          notification.success(`Payment ${rejected.paymentNumber} rejected`);
          setRejectDialogOpen(false);
          setRejectPayment(null);
          setRejectReason("");
        },
        onError: () => {
          notification.error("Failed to reject payment");
        },
      }
    );
  };

  if (isLoading) {
    return <LoadingAnimation fullScreen />;
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-600">Failed to load payments</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Payments</h1>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Track recorded, unreconciled, and refunded order payments
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <Button
          size="sm"
          variant="outline"
          onClick={() => { const today = todayInNairobi(); setFromDate(today); setToDate(today); }}
        >
          Today
        </Button>
        <Button size="sm" variant="ghost" onClick={() => { setFromDate(""); setToDate(""); }}>
          All dates
        </Button>
        <Button
          size="sm"
          variant={activeView === "all" ? "default" : "outline"}
          onClick={() => setActiveView("all")}
        >
          All
        </Button>
        <Button
          size="sm"
          variant={activeView === "pending-approval" ? "default" : "outline"}
          onClick={() => setActiveView("pending-approval")}
        >
          Pending Approval
        </Button>
        <Button
          size="sm"
          variant={activeView === "unreconciled" ? "default" : "outline"}
          onClick={() => setActiveView("unreconciled")}
        >
          Unreconciled
        </Button>
        <Button
          size="sm"
          variant={activeView === "refunds" ? "default" : "outline"}
          onClick={() => setActiveView("refunds")}
        >
          Refunds
        </Button>
      </div>

      {activeView === "pending-approval" && (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant={pendingTypeFilter === "all" ? "default" : "outline"} onClick={() => setPendingTypeFilter("all")}>
            All
          </Button>
          <Button size="sm" variant={pendingTypeFilter === "payment" ? "default" : "outline"} onClick={() => setPendingTypeFilter("payment")}>
            Payments
          </Button>
          <Button size="sm" variant={pendingTypeFilter === "refund" ? "default" : "outline"} onClick={() => setPendingTypeFilter("refund")}>
            Refund Requests
          </Button>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="pl-10"
            placeholder="Search code, receipt, order, invoice, customer, or phone"
            aria-label="Search payments"
          />
        </div>
        <Select value={paymentMethodFilter} onValueChange={setPaymentMethodFilter}>
          <SelectTrigger>
            <SelectValue placeholder="Filter by payment method" />
          </SelectTrigger>
          <SelectContent>
            {PAYMENT_METHODS.map((method) => (
              <SelectItem key={method} value={method}>
                {method === "all" ? "All Methods" : method}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">From date</p>
          <Input
            type="date"
            value={fromDate}
            max={toDate || undefined}
            onChange={(event) => setFromDate(event.target.value)}
            aria-label="Payments from date"
          />
        </div>
        <div className="space-y-1">
          <p className="text-xs text-muted-foreground">To date</p>
          <Input
            type="date"
            value={toDate}
            min={fromDate || undefined}
            onChange={(event) => setToDate(event.target.value)}
            aria-label="Payments to date"
          />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
        <Button
          size="sm"
          onClick={handleBulkReconcile}
          disabled={!hasBackendRows || !selectedPaymentIds.length || isBusy}
        >
          {bulkReconcilePayments.isPending
            ? "Reconciling..."
            : `Bulk Reconcile (${selectedPaymentIds.length})`}
        </Button>
        {!hasBackendRows && (
          <p className="text-sm text-muted-foreground">
            Reconcile actions are enabled when backend records are available.
          </p>
        )}
      </div>

      {/* Mobile card layout */}
      <div className="space-y-3 md:hidden">
        {displayRows.map((row) => {
          if (row.type === "flat") {
            const payment = row.payment;
            return (
              <div key={payment.id} className="rounded-lg border p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className={isRefundType(payment) ? "text-xs font-medium truncate" : "font-mono text-xs font-medium truncate"}>
                      {getPaymentDisplayLabel(payment)}
                    </p>
                    <ExpoPaymentBadge label={getExpoPaymentLabel(payment)} />
                    <p className="text-sm text-muted-foreground truncate">{payment.customerName}</p>
                    {payment.customerPhone && <p className="text-xs text-muted-foreground">{payment.customerPhone}</p>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Checkbox
                      checked={selectedPaymentIds.includes(payment.id)}
                      onCheckedChange={(checked) =>
                        togglePaymentSelection(payment.id, checked === true)
                      }
                      disabled={!hasBackendRows || payment.isReconciled || isBusy}
                      aria-label={`Select payment ${payment.paymentNumber}`}
                    />
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {(payment.approvalStatus === PAYMENT_APPROVAL_STATUS.Approved ||
                            payment.approvalStatus === PAYMENT_APPROVAL_STATUS.PartiallyApproved) && (
                          <>
                            <DropdownMenuItem
                              onClick={() => openReconcileDialog(payment)}
                              disabled={!hasBackendRows || payment.isReconciled || isBusy}
                            >
                              Reconcile
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => openReprintDialog(payment)}
                              disabled={
                                !hasBackendRows ||
                                printPaymentReceipt.isPending ||
                                (payment.orderIsWalkIn === true && payment.orderFullyPaid !== true)
                              }
                            >
                              Reprint Receipt
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => openRefundDialog(payment)}
                              disabled={!hasBackendRows || isRefundType(payment) || payment.isRefunded || processRefund.isPending}
                            >
                              Refund
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                  <div>
                    <span className="text-muted-foreground">Order: </span>
                    <span>{payment.orderNumber}</span>
                    <ExpoPaymentBadge label={getExpoPaymentLabel(payment)} />
                    {payment.invoiceNumber && <span className="block text-xs text-muted-foreground">{payment.invoiceNumber}</span>}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Amount: </span>
                    <span className="font-medium">{payment.amount.toLocaleString()}</span>
                    {((payment.orderWalletCreditAmount ?? 0) > 0 || (payment.orderPendingWalletCreditAmount ?? 0) > 0) && (
                      <div className="text-xs text-muted-foreground">
                        Order settlement: KES {(payment.orderAllocatedAmount ?? 0).toLocaleString()} allocated;
                        {" "}wallet +KES {(payment.orderWalletCreditAmount ?? 0).toLocaleString()} approved
                        {(payment.orderPendingWalletCreditAmount ?? 0) > 0 &&
                          `, KES ${payment.orderPendingWalletCreditAmount!.toLocaleString()} pending`}
                      </div>
                    )}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Method: </span>
                    <span>{payment.paymentMethod}</span>
                    {payment.paymentReference && (
                      <span className="block text-xs text-muted-foreground font-mono">{payment.paymentReference}</span>
                    )}
                    {payment.mpesaReceiptNumber && payment.mpesaReceiptNumber !== payment.paymentReference && (
                      <span className="block text-xs text-muted-foreground font-mono">M-Pesa: {payment.mpesaReceiptNumber}</span>
                    )}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Date: </span>
                    <span>{new Date(payment.paymentDate).toLocaleDateString()}</span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Badge variant={isRefundType(payment) ? "destructive" : "secondary"}>
                    {isRefundType(payment) ? "Refund" : "Payment"}
                  </Badge>
                  {payment.approvalStatus === PAYMENT_APPROVAL_STATUS.Approved ? (
                    <Badge variant="default">Approved</Badge>
                  ) : payment.approvalStatus === PAYMENT_APPROVAL_STATUS.PartiallyApproved ? (
                    <Badge variant="secondary">Partially Approved</Badge>
                  ) : payment.approvalStatus === PAYMENT_APPROVAL_STATUS.Rejected ? (
                    <Badge variant="destructive">Rejected</Badge>
                  ) : (
                    <Badge variant="outline">Pending Approval</Badge>
                  )}
                  <Badge variant={payment.isRefunded ? "outline" : "secondary"}>
                    {payment.isRefunded ? "Refunded" : "Not Refunded"}
                  </Badge>
                </div>
              </div>
            );
          }

          // Grouped multi-payment card — any order with ≥ 2 payments on the page
          const group = row;
          const isExpanded = expandedOrderIds.has(group.orderId);
          return (
            <div key={`group-${group.orderId}`} className="rounded-lg border overflow-hidden">
              {/* Group header */}
              <div
                className="p-4 bg-muted/40 cursor-pointer space-y-2"
                onClick={() => toggleOrderExpand(group.orderId)}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">{group.orderNumber}</p>
                      <p className="text-xs text-muted-foreground truncate">{group.customerName}</p>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-medium text-sm">{group.totalAmount.toLocaleString()}</p>
                    {((group.payments[0].orderWalletCreditAmount ?? 0) > 0 || (group.payments[0].orderPendingWalletCreditAmount ?? 0) > 0) && (
                      <p className="text-xs text-muted-foreground">
                        Order KES {(group.payments[0].orderAllocatedAmount ?? 0).toLocaleString()} · Wallet +KES {(group.payments[0].orderWalletCreditAmount ?? 0).toLocaleString()} approved
                        {(group.payments[0].orderPendingWalletCreditAmount ?? 0) > 0 &&
                          `, KES ${group.payments[0].orderPendingWalletCreditAmount!.toLocaleString()} pending`}
                      </p>
                    )}
                    <Badge variant="secondary" className="text-[10px]">
                      {group.payments.length} payments · {group.hasMixedMethods ? "Split tender" : "Same method"}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <Badge variant={group.allApproved ? "default" : "outline"}>
                    {group.approvedCount}/{group.payments.length} approved
                  </Badge>
                  {group.allApproved && (group.orderFullyPaid || group.hasPrintableDocument) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        openReprintDialog(group.payments[0]);
                      }}
                      disabled={!hasBackendRows || printPaymentReceipt.isPending}
                    >
                      Print Receipt
                    </Button>
                  )}
                </div>
              </div>

              {/* Expanded child cards */}
              {isExpanded && (
                <div className="divide-y">
                  {group.payments.map((payment) => (
                    <div key={payment.id} className="p-3 pl-8 space-y-2 bg-muted/20">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className={isRefundType(payment) ? "text-xs font-medium truncate" : "font-mono text-xs truncate"}>
                            {getPaymentDisplayLabel(payment)}
                          </p>
                          <p className="text-xs text-muted-foreground">{payment.paymentMethod}</p>
                          {payment.paymentReference && (
                            <p className="text-xs text-muted-foreground font-mono">{payment.paymentReference}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="font-medium text-sm">{payment.amount.toLocaleString()}</span>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7">
                                <MoreHorizontal className="h-3.5 w-3.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {payment.approvalStatus === PAYMENT_APPROVAL_STATUS.PendingApproval && (
                                <>
                                  <DropdownMenuItem
                                    onClick={() => handleApprovePayment(payment)}
                                    disabled={approvePaymentMut.isPending}
                                  >
                                    Approve
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => openRejectDialog(payment)}
                                    disabled={rejectPaymentMut.isPending}
                                  >
                                    Reject
                                  </DropdownMenuItem>
                                </>
                              )}
                              {(payment.approvalStatus === PAYMENT_APPROVAL_STATUS.Approved ||
                            payment.approvalStatus === PAYMENT_APPROVAL_STATUS.PartiallyApproved) && (
                                <>
                                  <DropdownMenuItem
                                    onClick={() => openReconcileDialog(payment)}
                                    disabled={!hasBackendRows || payment.isReconciled || isBusy}
                                  >
                                    Reconcile
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => openRefundDialog(payment)}
                                    disabled={!hasBackendRows || isRefundType(payment) || payment.isRefunded || processRefund.isPending}
                                  >
                                    Refund
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {payment.approvalStatus === PAYMENT_APPROVAL_STATUS.Approved ? (
                          <Badge variant="default" className="text-[10px]">Approved</Badge>
                        ) : payment.approvalStatus === PAYMENT_APPROVAL_STATUS.PartiallyApproved ? (
                    <Badge variant="secondary">Partially Approved</Badge>
                  ) : payment.approvalStatus === PAYMENT_APPROVAL_STATUS.Rejected ? (
                          <Badge variant="destructive" className="text-[10px]">Rejected</Badge>
                        ) : (
                          <Badge variant="outline" className="text-[10px]">Pending</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Desktop table layout */}
      <div className="hidden md:block rounded-lg border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={allUnreconciledSelected}
                  onCheckedChange={(checked) =>
                    handleSelectAllUnreconciled(checked === true)
                  }
                  disabled={!hasBackendRows || !unreconciledRows.length || isBusy}
                  aria-label="Select unreconciled payments"
                />
              </TableHead>
              <TableHead>Payment #</TableHead>
              <TableHead>Order #</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Type</TableHead>
              <TableHead className="hidden lg:table-cell">Method</TableHead>
              <TableHead>Date</TableHead>
              <TableHead className="hidden lg:table-cell">Receipt #</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden lg:table-cell">Refund</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayRows.map((row) => {
              if (row.type === "flat") {
                const payment = row.payment;
                return (
                  <TableRow key={payment.id}>
                    <TableCell>
                      <Checkbox
                        checked={selectedPaymentIds.includes(payment.id)}
                        onCheckedChange={(checked) =>
                          togglePaymentSelection(payment.id, checked === true)
                        }
                        disabled={!hasBackendRows || payment.isReconciled || isBusy}
                        aria-label={`Select payment ${payment.paymentNumber}`}
                      />
                    </TableCell>
                    <TableCell className={isRefundType(payment) ? "text-xs font-medium" : "font-mono text-xs"}>
                      {getPaymentDisplayLabel(payment)}
                    </TableCell>
                    <TableCell>
                      <div>{payment.orderNumber}</div>
                      <ExpoPaymentBadge label={getExpoPaymentLabel(payment)} />
                      {payment.invoiceNumber && <div className="text-xs text-muted-foreground">{payment.invoiceNumber}</div>}
                    </TableCell>
                    <TableCell>
                      <div>{payment.customerName}</div>
                      {payment.customerPhone && <div className="text-xs text-muted-foreground">{payment.customerPhone}</div>}
                    </TableCell>
                    <TableCell>
                      <div>{payment.amount.toLocaleString()}</div>
                      {((payment.orderWalletCreditAmount ?? 0) > 0 || (payment.orderPendingWalletCreditAmount ?? 0) > 0) && (
                        <div className="text-xs text-muted-foreground whitespace-nowrap">
                          Order: KES {(payment.orderAllocatedAmount ?? 0).toLocaleString()}
                          <br />Wallet: +KES {(payment.orderWalletCreditAmount ?? 0).toLocaleString()} approved
                          {(payment.orderPendingWalletCreditAmount ?? 0) > 0 &&
                            `; KES ${payment.orderPendingWalletCreditAmount!.toLocaleString()} pending`}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={isRefundType(payment) ? "destructive" : "secondary"}>
                        {isRefundType(payment) ? "Refund" : "Payment"}
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <div>{payment.paymentMethod}</div>
                      {payment.paymentReference && (
                        <div className="text-xs text-muted-foreground font-mono">{payment.paymentReference}</div>
                      )}
                      {payment.mpesaReceiptNumber && payment.mpesaReceiptNumber !== payment.paymentReference && (
                        <div className="text-xs text-muted-foreground font-mono">M-Pesa: {payment.mpesaReceiptNumber}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(payment.paymentDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell font-mono text-xs">
                      {payment.receiptNumber || "—"}
                    </TableCell>
                    <TableCell>
                      {payment.approvalStatus === PAYMENT_APPROVAL_STATUS.Approved ? (
                        <Badge variant="default">Approved</Badge>
                      ) : payment.approvalStatus === PAYMENT_APPROVAL_STATUS.PartiallyApproved ? (
                    <Badge variant="secondary">Partially Approved</Badge>
                  ) : payment.approvalStatus === PAYMENT_APPROVAL_STATUS.Rejected ? (
                        <Badge variant="destructive" title={payment.rejectionReason ?? undefined}>Rejected</Badge>
                      ) : (
                        <Badge variant="outline">Pending Approval</Badge>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <Badge variant={payment.isRefunded ? "outline" : "secondary"}>
                        {payment.isRefunded ? "Refunded" : "Not Refunded"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {payment.approvalStatus === PAYMENT_APPROVAL_STATUS.PendingApproval && (
                            <>
                              <DropdownMenuItem
                                onClick={() => handleApprovePayment(payment)}
                                disabled={approvePaymentMut.isPending}
                              >
                                Approve
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => openRejectDialog(payment)}
                                disabled={rejectPaymentMut.isPending}
                              >
                                Reject
                              </DropdownMenuItem>
                            </>
                          )}
                          {(payment.approvalStatus === PAYMENT_APPROVAL_STATUS.Approved ||
                            payment.approvalStatus === PAYMENT_APPROVAL_STATUS.PartiallyApproved) && (
                            <>
                              <DropdownMenuItem
                                onClick={() => openReconcileDialog(payment)}
                                disabled={!hasBackendRows || payment.isReconciled || isBusy}
                              >
                                Reconcile
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => openReprintDialog(payment)}
                                disabled={!hasBackendRows || printPaymentReceipt.isPending}
                              >
                                Reprint Receipt
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => openRefundDialog(payment)}
                                disabled={
                                  !hasBackendRows ||
                                  isRefundType(payment) ||
                                  payment.isRefunded ||
                                  processRefund.isPending ||
                                  printPaymentReceipt.isPending
                                }
                              >
                                Refund
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              }

              // Grouped multi-payment row — any order with ≥ 2 payments on the page
              const group = row;
              const isExpanded = expandedOrderIds.has(group.orderId);
              return (
                <Fragment key={`group-${group.orderId}`}>
                  {/* Group header row */}
                  <TableRow
                    className="bg-muted/40 hover:bg-muted/60 cursor-pointer"
                    onClick={() => toggleOrderExpand(group.orderId)}
                  >
                    <TableCell>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                    </TableCell>
                    <TableCell className="font-medium text-xs">
                      <Badge variant="secondary" className="text-[10px]">
                        {group.payments.length} payments
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      <div>{group.orderNumber}</div>
                      <ExpoPaymentBadge label={group.expoName} />
                    </TableCell>
                    <TableCell>{group.customerName}</TableCell>
                    <TableCell className="font-medium">
                      <div>{group.totalAmount.toLocaleString()}</div>
                      {((group.payments[0].orderWalletCreditAmount ?? 0) > 0 || (group.payments[0].orderPendingWalletCreditAmount ?? 0) > 0) && (
                        <div className="text-xs text-muted-foreground whitespace-nowrap">
                          Order: KES {(group.payments[0].orderAllocatedAmount ?? 0).toLocaleString()}
                          <br />Wallet: +KES {(group.payments[0].orderWalletCreditAmount ?? 0).toLocaleString()} approved
                          {(group.payments[0].orderPendingWalletCreditAmount ?? 0) > 0 &&
                            `; KES ${group.payments[0].orderPendingWalletCreditAmount!.toLocaleString()} pending`}
                        </div>
                      )}
                    </TableCell>
                    <TableCell />
                    <TableCell className="hidden lg:table-cell">
                      <span className="text-muted-foreground text-xs">
                        {group.hasMixedMethods ? "Split tender" : "Multiple payments"}
                      </span>
                    </TableCell>
                    <TableCell>
                      {new Date(group.payments[0].paymentDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell" />
                    <TableCell>
                      <Badge variant={group.allApproved ? "default" : "outline"}>
                        {group.approvedCount}/{group.payments.length} approved
                      </Badge>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell" />
                    <TableCell className="text-right">
                      {group.allApproved && (group.orderFullyPaid || group.hasPrintableDocument) && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            openReprintDialog(group.payments[0]);
                          }}
                          disabled={!hasBackendRows || printPaymentReceipt.isPending}
                        >
                          Print Receipt
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>

                  {/* Expanded child rows */}
                  {isExpanded && group.payments.map((payment) => (
                    <TableRow key={payment.id} className="bg-muted/20">
                      <TableCell className="pl-8">
                        <Checkbox
                          checked={selectedPaymentIds.includes(payment.id)}
                          onCheckedChange={(checked) =>
                            togglePaymentSelection(payment.id, checked === true)
                          }
                          disabled={!hasBackendRows || payment.isReconciled || isBusy}
                          aria-label={`Select payment ${payment.paymentNumber}`}
                        />
                      </TableCell>
                      <TableCell className={isRefundType(payment) ? "text-xs font-medium pl-8" : "font-mono text-xs pl-8"}>
                        {getPaymentDisplayLabel(payment)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">↳</TableCell>
                      <TableCell />
                      <TableCell>{payment.amount.toLocaleString()}</TableCell>
                      <TableCell>
                        <Badge variant={isRefundType(payment) ? "destructive" : "secondary"}>
                          {isRefundType(payment) ? "Refund" : "Payment"}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <div>{payment.paymentMethod}</div>
                        {payment.paymentReference && (
                          <div className="text-xs text-muted-foreground font-mono">{payment.paymentReference}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        {new Date(payment.paymentDate).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell font-mono text-xs">
                        {payment.receiptNumber || "—"}
                      </TableCell>
                      <TableCell>
                        {payment.approvalStatus === PAYMENT_APPROVAL_STATUS.Approved ? (
                          <Badge variant="default">Approved</Badge>
                        ) : payment.approvalStatus === PAYMENT_APPROVAL_STATUS.PartiallyApproved ? (
                    <Badge variant="secondary">Partially Approved</Badge>
                  ) : payment.approvalStatus === PAYMENT_APPROVAL_STATUS.Rejected ? (
                          <Badge variant="destructive" title={payment.rejectionReason ?? undefined}>Rejected</Badge>
                        ) : (
                          <Badge variant="outline">Pending</Badge>
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <Badge variant={payment.isRefunded ? "outline" : "secondary"}>
                          {payment.isRefunded ? "Refunded" : "Not Refunded"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {payment.approvalStatus === PAYMENT_APPROVAL_STATUS.PendingApproval && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => handleApprovePayment(payment)}
                                  disabled={approvePaymentMut.isPending}
                                >
                                  Approve
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => openRejectDialog(payment)}
                                  disabled={rejectPaymentMut.isPending}
                                >
                                  Reject
                                </DropdownMenuItem>
                              </>
                            )}
                            {(payment.approvalStatus === PAYMENT_APPROVAL_STATUS.Approved ||
                            payment.approvalStatus === PAYMENT_APPROVAL_STATUS.PartiallyApproved) && (
                              <>
                                <DropdownMenuItem
                                  onClick={() => openReconcileDialog(payment)}
                                  disabled={!hasBackendRows || payment.isReconciled || isBusy}
                                >
                                  Reconcile
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => openRefundDialog(payment)}
                                  disabled={
                                  !hasBackendRows ||
                                  isRefundType(payment) ||
                                  payment.isRefunded ||
                                    processRefund.isPending
                                  }
                                >
                                  Refund
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {filteredRows.length === 0 ? (
        <div className="rounded-lg border p-8 text-center text-sm text-muted-foreground">
          {searchTerm.trim()
            ? `No payments match “${searchTerm.trim()}”. Try another code, phone format, or customer name.`
            : "No payments found for the selected filters."}
        </div>
      ) : (
        <PaginationBar
          currentPage={currentPage}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => { setPageSize(size); setCurrentPage(1); }}
        />
      )}

      <Dialog open={reconcileDialogOpen} onOpenChange={setReconcileDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reconcile Payment</DialogTitle>
            <DialogDescription>
              {activePayment
                ? `Confirm reconciliation for ${activePayment.paymentNumber}`
                : "Confirm payment reconciliation"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">Notes (optional)</p>
            <Textarea
              placeholder="Add reconciliation notes"
              value={reconcileNotes}
              onChange={(event) => setReconcileNotes(event.target.value)}
              disabled={reconcilePayment.isPending}
            />
          </div>

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setReconcileDialogOpen(false)}
              disabled={reconcilePayment.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleSingleReconcile} disabled={reconcilePayment.isPending}>
              {reconcilePayment.isPending ? "Reconciling..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReprintPaymentReceiptDialog
        open={reprintDialogOpen}
        onOpenChange={(open) => {
          setReprintDialogOpen(open);
          if (!open) {
            setReprintPayment(null);
          }
        }}
        payment={reprintPayment}
        isSubmitting={printPaymentReceipt.isPending}
        onSubmit={handleReprintReceipt}
      />

      <Dialog
        open={refundDialogOpen}
        onOpenChange={(open) => {
          setRefundDialogOpen(open);
          if (!open) {
            resetRefundForm();
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Process Refund</DialogTitle>
            <DialogDescription>
              {refundPayment
                ? `Create refund for ${refundPayment.paymentNumber}`
                : "Create refund transaction"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Amount</p>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={refundForm.amount}
                onChange={(event) =>
                  setRefundForm((prev) => ({ ...prev, amount: event.target.value }))
                }
                disabled={processRefund.isPending}
              />
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Reason</p>
              <Textarea
                value={refundForm.reason}
                onChange={(event) =>
                  setRefundForm((prev) => ({ ...prev, reason: event.target.value }))
                }
                placeholder="Reason for refund"
                disabled={processRefund.isPending}
              />
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Payment Method</p>
              <Select
                value={refundForm.paymentMethod}
                onValueChange={(value) =>
                  setRefundForm((prev) => ({ ...prev, paymentMethod: value }))
                }
                disabled={processRefund.isPending}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select method" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.filter((method) => method !== "all").map((method) => (
                    <SelectItem key={method} value={method}>
                      {method}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Payment Reference</p>
              <Input
                value={refundForm.paymentReference}
                onChange={(event) =>
                  setRefundForm((prev) => ({ ...prev, paymentReference: event.target.value }))
                }
                placeholder="Reference"
                disabled={processRefund.isPending}
              />
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Notes</p>
              <Textarea
                value={refundForm.notes}
                onChange={(event) =>
                  setRefundForm((prev) => ({ ...prev, notes: event.target.value }))
                }
                placeholder="Additional notes"
                disabled={processRefund.isPending}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setRefundDialogOpen(false)}
              disabled={processRefund.isPending}
            >
              Cancel
            </Button>
            <Button onClick={handleRefund} disabled={processRefund.isPending}>
              {processRefund.isPending ? "Processing..." : "Process Refund"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Payment Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onOpenChange={(open) => {
          setRejectDialogOpen(open);
          if (!open) {
            setRejectPayment(null);
            setRejectReason("");
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reject Payment</DialogTitle>
            <DialogDescription>
              {rejectPayment
                ? `Reject ${rejectPayment.paymentNumber} (${rejectPayment.amount.toLocaleString()} KES)`
                : "Reject payment"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Reason <span className="text-destructive">*</span>
            </p>
            <Textarea
              placeholder="Why is this payment being rejected?"
              value={rejectReason}
              onChange={(event) => setRejectReason(event.target.value)}
              disabled={rejectPaymentMut.isPending}
            />
          </div>

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setRejectDialogOpen(false)}
              disabled={rejectPaymentMut.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleRejectPayment}
              disabled={rejectPaymentMut.isPending || !rejectReason.trim()}
            >
              {rejectPaymentMut.isPending ? "Rejecting..." : "Reject Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
